import clsx from 'clsx';
import * as R from 'ramda';
import { useEffect, useRef } from 'react';
import ReactQuill, { ReactQuillProps } from 'react-quill';
import i18n from 'helpers/i18n';
import messages from './messages';

type QuillModules = {
  [key: string]: any;
};

type QuillFormats = string[];

const DEFAULT_MODULES: QuillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'bullet' }, { list: 'ordered' }],
    ['link']
  ],
  clipboard: {
    matchVisual: false
  },
  keyboard: {
    bindings: {
      tab: null,
      'remove tab': null,
      indent: null,
      outdent: null
    }
  }
};

const DEFAULT_FORMATS: QuillFormats = [
  'bold',
  'italic',
  'underline',
  'bullet',
  'list',
  'link'
];

export interface RichTextEditorProps extends ReactQuillProps {
  /** Custom className. */
  className?: string;

  /** If `true`, the editor text can only be read. */
  readOnly?: boolean;

  /** If `true`, the editor colors will turn to error red. */
  hasError?: boolean;

  /** Text content for the input. */
  value?: string;

  /** If `true`, headings will be allowed in the toolbar. */
  headings?: boolean;
}

// Quill's default toolbar buttons ship as bare icons with no accessible
// name, so screen reader users hear nothing when focusing them.
function labelToolbarButtons(container: HTMLElement) {
  const labels: [string, string][] = [
    ['button.ql-bold', i18n.ft(messages.toolbar.bold)],
    ['button.ql-italic', i18n.ft(messages.toolbar.italic)],
    ['button.ql-underline', i18n.ft(messages.toolbar.underline)],
    ['button.ql-list[value="bullet"]', i18n.ft(messages.toolbar.bulletedList)],
    ['button.ql-list[value="ordered"]', i18n.ft(messages.toolbar.numberedList)],
    ['button.ql-link', i18n.ft(messages.toolbar.link)],
    ['button.ql-header[value="1"]', i18n.ft(messages.toolbar.heading)]
  ];

  labels.forEach(([selector, label]) => {
    container
      .querySelectorAll<HTMLButtonElement>(selector)
      .forEach(button => button.setAttribute('aria-label', label));
  });
}

function RichTextEditor({ headings, ...props }: RichTextEditorProps) {
  const quillRef = useRef<ReactQuill>(null);

  const classes = clsx('RichTextEditor', props.className, {
    'RichTextEditor--readOnly': props.readOnly,
    'RichTextEditor--error': props.hasError
  });

  const restProps = R.omit(['className'], props);

  let modules = R.clone(DEFAULT_MODULES);
  let formats = R.clone(DEFAULT_FORMATS);

  if (headings) {
    modules.toolbar = [[{ header: 1 }], ...modules.toolbar];
    formats = ['header', ...formats];
  }

  // No dependency array: Quill rebuilds its toolbar DOM whenever it
  // regenerates internally (e.g. `value` arriving asynchronously after
  // mount), so labels are reapplied on every render rather than once.
  useEffect(() => {
    const toolbar = quillRef.current?.getEditor().getModule('toolbar');
    const container = toolbar?.container as HTMLElement | undefined;

    if (container) {
      labelToolbarButtons(container);
    }
  });

  return (
    <ReactQuill
      ref={quillRef}
      {...restProps}
      modules={modules}
      formats={formats}
      className={classes}
    />
  );
}

export default RichTextEditor;
